import React, { useState, useMemo, useEffect } from "react";
import { usePodcast } from '../../context/PodcastContext';
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Stack,
    Divider,
    Alert,
    Radio,
    RadioGroup,
    LinearProgress,
    FormControlLabel,
    Checkbox,
    Chip,
} from "@mui/material";
import {
    SelfImprovement,
    MusicNote,
    Headphones,
    Bolt,
    CampaignRounded,
    CheckCircle,
    ArrowForward,
    ArrowBack,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { trackCTA } from "../../service/usercta";
import RejuvenateSoundsLogo from "../../assets/rejuvenate-sounds.png";
import EchoSoundscapesLogo from "../../assets/echoSoundscape.png";
import Day1Img from "../../assets/selfcare/day1.jpg";
import Day2Img from "../../assets/selfcare/day2.jpg";
import Day3Img from "../../assets/selfcare/day3.jpg";
import Day4Img from "../../assets/selfcare/day4.webp";
import Day5Img from "../../assets/selfcare/day5.jpg";
import { saveSelfCareCampaign, getSelfCareCampaignStatus } from "../../service/selfcareCampaign";
import { allQuickActions, QuickActionModal } from "./QuickActions";

/* ─────────────────────────────────────────────────────────────────────────
   Design tokens — same gradient family used across the app's campaign
   cards (Campaign.jsx) so this reads as part of the same product, with a
   calmer two-stop version (sage → lavender) suited to a self-care theme
   rather than the music campaign's three-stop version.
   ───────────────────────────────────────────────────────────────────────── */
const tokens = {
    gradient: "linear-gradient(135deg, #2f9e6f, #6d5bd0)",
    primary: "#2f9e6f",
    primaryLight: "#E7F5EE",
    accent: "#6d5bd0",
    border: "#E8EAF0",
    textSecondary: "#5f6368",
};
// Define the real campaign window using actual dates
const CAMPAIGN_START = new Date(2026, 6, 20); // Month is 0-indexed: 6 = July

const DATE_TO_DAY = { 20: 1, 21: 2, 22: 3, 23: 4, 24: 5 }; // calendar date → day number (1-5)

const getTodayDateOnly = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};


const DAY_ORDER = [1, 2, 3, 4, 5];

const DAY_META = {
    1: { label: "Day 1 · Mon 20 Jul", short: "Myth vs Reality Quiz", icon: <SelfImprovement fontSize="small" /> },
    2: { label: "Day 2 · Tue 21 Jul", short: "Soundscapes", icon: <MusicNote fontSize="small" /> },
    3: { label: "Day 3 · Wed 22 Jul", short: "Podcast", icon: <Headphones fontSize="small" /> },
    4: { label: "Day 4 · Thu 23 Jul", short: "Quick Reset", icon: <Bolt fontSize="small" /> },
    5: { label: "Day 5 · Fri 24 Jul", short: "Tell Us Out Loud", icon: <CampaignRounded fontSize="small" /> },
};

const QUIZ_QUESTIONS = [
    {
        q: "“Self-care means taking a break from work.” Which statement is actually true?",
        options: [
            "Self-care means taking long breaks whenever work feels stressful.",
            "Self-care begins with paying attention to your wellbeing and responding to your needs before burnout sets in.",
            "Self-care is only necessary when work becomes overwhelming.",
        ],
        correct: 1,
    },
    {
        q: "“I know myself well enough.” Which statement is actually true?",
        options: [
            "Most people are able to identify stress before it affects them.",
            "Once you know your personality, self-awareness doesn't require much effort.",
            "Many people notice stress only after it begins affecting their sleep, mood, energy, or relationships.",
        ],
        correct: 2,
    },
    {
        q: "“Self-care is doing what feels good.” Which statement is actually true?",
        options: [
            "Self-care is choosing actions that support your wellbeing, even if they feel uncomfortable in the moment.",
            "Self-care should always feel relaxing and enjoyable.",
            "If an activity feels difficult, it cannot be considered self-care.",
        ],
        correct: 0,
    },
    {
        q: "“I don't have time for self-care.” Which statement is actually true?",
        options: [
            "Self-care only works when you can dedicate at least an hour to it.",
            "Self-care often requires major lifestyle changes and extra time.",
            "Self-care can be built into small intentional pauses and actions throughout the day.",
        ],
        correct: 2,
    },
    {
        q: "“Self-care is a solo activity.” Which statement is actually true?",
        options: [
            "Self-care is most effective when done completely independently.",
            "Meaningful connection and support from others can be an important part of self-care.",
            "Self-care should focus only on individual habits and routines.",
        ],
        correct: 1,
    },
];

const FINAL_POLL_OPTIONS = [
    { id: "quiz", label: "Myth vs Reality Quiz" },
    { id: "soundscapes", label: "echo Soundscapes" },
    { id: "podcast", label: "Podcast on Self-care" },
    { id: "quickActions", label: "Quick Actions" },
];

const REMINDERS = [
    "Self-care isn't selfish",
    "Self-care does not require hours of free time",
    "Self-care isn't always comfortable",
    "Self-care is not something you earn",
    "Self-care is a practice.",
];


const SelfCareCampaign = ({ onOpenPodcast: onOpenPodcastProp, podcastPlayedMs: podcastPlayedMsProp = 0 }) => {
    const podcastCtx = usePodcast();
    const onOpenPodcast = podcastCtx?.handleListenNow ?? onOpenPodcastProp;
    const podcastPlayedMs = podcastCtx?.podcastPlayedMs ?? podcastPlayedMsProp;
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const accountId = user.account_id || user.accountId;
    const userId = user.id;
    const urlDayOverride = new URLSearchParams(window.location.search).get("day");
    const parsedUrlDay = urlDayOverride ? Number(urlDayOverride) : null;
    const validUrlDay = (parsedUrlDay >= 1 && parsedUrlDay <= 5) ? parsedUrlDay : null;
    const todayDayNum = validUrlDay ?? (DATE_TO_DAY[getTodayDateOnly().getDate()] ?? null);


    const [completedDays, setCompletedDays] = useState({});

    // Local UI state for the in-card challenges
    const [quizStep, setQuizStep] = useState(0);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [quizFinished, setQuizFinished] = useState(false);
    const [podcastFeedback, setPodcastFeedback] = useState(null);
    const [pollSelections, setPollSelections] = useState([]);
    const [soundscapeSelections, setSoundscapeSelections] = useState([]);
    useEffect(() => {
        if (!userId) return;
        getSelfCareCampaignStatus(userId)
            .then((data) => {
                if (data?.completed_days && Array.isArray(data.completed_days)) {
                    const serverDays = {};
                    data.completed_days.forEach((d) => { serverDays[d] = true; });
                    setCompletedDays((prev) => ({ ...serverDays, ...prev }));
                }
            })
            .catch(() => {});
    }, []);

    const isWithinCampaignMonth = (() => {
        if (validUrlDay) return true;
        const now = getTodayDateOnly();
        return now.getFullYear() === CAMPAIGN_START.getFullYear() &&
            now.getMonth() === CAMPAIGN_START.getMonth();
    })();
    const markDayComplete = (day, ctaName) => {
        trackCTA(ctaName, JSON.parse(localStorage.getItem("user") || "{}"));
        setCompletedDays((prev) => ({ ...prev, [day]: true }));
    };
    const DAY_IMAGES = {
        1: Day1Img,
        2: Day2Img,
        3: Day3Img,
        4: Day4Img,
        5: Day5Img,
    };

    // Converts { 0: 0, 1: 1, 2: 2, 3: 0, 4: 1 } (0-indexed step -> 0-indexed option)
    // into      { Q1: 1, Q2: 2, Q3: 3, Q4: 1, Q5: 2 } (1-indexed, report-friendly)
    const formatQuizAnswers = (answers) => {
        const formatted = {};
        Object.keys(answers).forEach((step) => {
            const questionNum = Number(step) + 1;
            const selectedOptionNum = answers[step] + 1;
            formatted[`Q${questionNum}`] = selectedOptionNum;
        });
        return formatted;
    };




    const DayImage = ({ day }) => {
        const src = DAY_IMAGES[day];
        if (!src) return null;

        return (
            <Box
                sx={{
                    width: "100%",
                    // height: { xs: 140, sm: 180, md: 210 },
                    height: { xs: 90, sm: 120, md: 140 },
                    borderRadius: "14px",
                    overflow: "hidden",
                    mb: 2,
                    bgcolor: tokens.primaryLight,
                }}
            >
                <Box
                    component="img"
                    src={src}
                    alt={DAY_META[day]?.short || "Self-care"}
                    loading="lazy"
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
            </Box>
        );
    };

    //   // Campaign only shows once the window has opened (20th onward)
    const campaignStarted = isWithinCampaignMonth && todayDayNum !== null;

    // Clamp so we never look past Day 5 for "what should be unlocked"
    const targetDay = Math.min(todayDayNum ?? 0, 5);

    const firstIncompleteDay = useMemo(
        () => DAY_ORDER.find((d) => d <= targetDay && !completedDays[d]),
        [completedDays, targetDay]
    );

    if (!campaignStarted) return null;

    // All days up to today's are done
    // if (!firstIncompleteDay) {
    //     if (targetDay === 24) {
    //         return <CongratsCard />;
    //     }
    //     return <WaitingCard nextDay={DAY_ORDER[DAY_ORDER.indexOf(targetDay) + 1]} />;
    // }


    const activeDay = firstIncompleteDay;
    const isCatchUp = activeDay < todayDayNum;

    // Always show the campaign.
    // The active day is simply the first incomplete day.
    // const activeDay = useMemo(
    //     () => DAY_ORDER.find((d) => !completedDays[d]),
    //     [completedDays]
    // );

    // // Show congratulations when all 5 days are completed.
    // if (!activeDay) {
    //     return <CongratsCard />;
    // }

    // No catch-up mode when date restrictions are removed.
    // const isCatchUp = false;
    const resetLocalChallengeState = () => {
        setQuizStep(0);
        setQuizAnswers({});
        setQuizFinished(false);
        setPodcastFeedback(null);
        setPollSelections([]);
    };

    const handleCompleteAndAdvance = (day, ctaName) => {
        markDayComplete(day, ctaName);
        resetLocalChallengeState();
    };

    const SOUNDSCAPE_ID_MAP = {
        "/rejuvenate-sounds": "rejuvenate_sounds",
        "/echo-soundscapes": "echo_soundscapes",
    };

    const getQuickActionId = (path) => {
        try {
            const url = new URL(path, window.location.origin);
            return url.searchParams.get("tab") || path;
        } catch {
            return path;
        }
    };






    const handleSaveCampaign = async (dayNumber, data = {}) => {
        if (!accountId) {
            console.error("Missing accountId — cannot save campaign response");
            // setSnackbar({ open: true, message: "Unable to save: account not found.", severity: "error" });
            return;
        }

        try {
            const payload = {
                user_id: userId || 0,
                account_id: accountId,
                day_number: dayNumber,
                quiz_answers: data.quiz_answers || null,
                // soundscape_choice: data.soundscape_choice || "",
                soundscape_choice: data.soundscape_choice || [],
                podcast_feedback: data.podcast_feedback || "",
                quick_action: data.quick_action || "",
                final_poll: data.final_poll || null,
                completed: true,
            };

            const response = await saveSelfCareCampaign(payload);
            console.log("Saved:", response);
        } catch (error) {
            console.error("Failed to save campaign:", error);
            // setSnackbar({ open: true, message: "Failed to save your response. Please try again.", severity: "error" });
        }
    };

    // const handleSoundscapeChoice = (path) => {
    //     const soundscapeChoice = SOUNDSCAPE_ID_MAP[path] || path;

    //     // Always persist this specific choice — every click should hit the DB
    //     handleSaveCampaign(2, { soundscape_choice: soundscapeChoice });

    //     // Only mark the day complete / advance state on the FIRST choice
    //     if (!completedDays[21]) {
    //         handleCompleteAndAdvance(21, "campaign_selfcare_day2_soundscape");
    //     } else {
    //         // Optional: still track the repeat interaction for analytics
    //         trackCTA("campaign_selfcare_day2_soundscape_replay", JSON.parse(localStorage.getItem("user") || "{}"));
    //     }

    //     navigate(path);
    // };

    const handleSoundscapeChoice = (path) => {
    const soundscapeChoice = SOUNDSCAPE_ID_MAP[path] || path;
    const updated = [...new Set([...soundscapeSelections, soundscapeChoice])];
    setSoundscapeSelections(updated);

    handleSaveCampaign(2, { soundscape_choice: updated });

    if (!completedDays[2]) {
        handleCompleteAndAdvance(2, "campaign_selfcare_day2_soundscape");
    } else {
        trackCTA(
            "campaign_selfcare_day2_soundscape_replay",
            JSON.parse(localStorage.getItem("user") || "{}")
        );
    }

    navigate(path);
};

    if (!firstIncompleteDay) {
        if (targetDay === 5) {
            return <CongratsCard />;
        }

        return <WaitingCard nextDay={DAY_ORDER[DAY_ORDER.indexOf(targetDay) + 1]} />;
    }

    return (


        <Box sx={{ maxWidth: 560, mx: "auto", px: { xs: 1, sm: 0 } }}>
            <Card
                elevation={0}
                sx={{
                    borderRadius: "16px",
                    border: `1px solid ${tokens.border}`,
                    overflow: "hidden",
                    transition: "box-shadow 0.2s ease",
                    "&:hover": { boxShadow: "0 8px 32px rgba(47,158,111,0.10)" },
                    animation: "scCardIn 0.45s ease-out both",
                    "@keyframes scCardIn": {
                        from: { opacity: 0, transform: "translateY(18px)" },
                        to: { opacity: 1, transform: "translateY(0)" },
                    },
                }}
            >
                {/* ── Hero image ── */}
                <Box sx={{ position: "relative", height: { xs: 190, sm: 230 }, overflow: "hidden" }}>
                    <Box
                        component="img"
                        src={DAY_IMAGES[activeDay]}
                        alt={DAY_META[activeDay]?.short || "Self-care"}
                        loading="lazy"
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                    {/* Gradient overlay */}
                    <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.68) 100%)" }} />

                    {/* Shimmer sweep over hero */}
                    <Box sx={{
                        position: "absolute", inset: 0, pointerEvents: "none",
                        background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.07) 50%, transparent 60%)",
                        backgroundSize: "200% 100%",
                        animation: "heroShimmer 3.5s ease-in-out infinite",
                        "@keyframes heroShimmer": {
                            "0%": { backgroundPosition: "200% 0" },
                            "100%": { backgroundPosition: "-200% 0" },
                        },
                    }} />

                    {/* Campaign badge — top left */}
                    <Box sx={{
                        position: "absolute", top: 14, left: 14,
                        display: "inline-flex", alignItems: "center", gap: 0.5,
                        bgcolor: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255,255,255,0.32)",
                        borderRadius: "20px", px: 1.25, py: 0.4,
                    }}>
                        <SelfImprovement sx={{ fontSize: 12, color: "#fff" }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#fff", letterSpacing: "0.07em", textTransform: "uppercase", fontSize: "0.6rem" }}>
                            Campaign
                        </Typography>
                    </Box>

                    {/* Day progress pills — top right */}
                    <Stack direction="row" spacing={0.5} sx={{ position: "absolute", top: 16, right: 14 }}>
                        {DAY_ORDER.map((d) => (
                            <Box
                                key={d}
                                title={DAY_META[d].label}
                                sx={{
                                    width: completedDays[d] || d === activeDay ? 18 : 6,
                                    height: 6,
                                    borderRadius: 3,
                                    transition: "all 0.3s ease",
                                    bgcolor: completedDays[d] ? tokens.primary : d === activeDay ? "#fff" : "rgba(255,255,255,0.35)",
                                    ...(d === activeDay && !completedDays[d] && {
                                        animation: "pillGlow 1.8s ease-in-out infinite",
                                        "@keyframes pillGlow": {
                                            "0%,100%": { boxShadow: "0 0 0 0 rgba(255,255,255,0.6)" },
                                            "50%": { boxShadow: "0 0 0 4px rgba(255,255,255,0)" },
                                        },
                                    }),
                                }}
                            />
                        ))}
                    </Stack>

                    {/* Bottom overlay: campaign name + day label */}
                    <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, px: 2, pb: 2, pt: 4 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2, textShadow: "0 2px 10px rgba(0,0,0,0.55)", mb: 0.3 }}>
                            Self-Care Unfiltered
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.88)", textShadow: "0 1px 4px rgba(0,0,0,0.4)", fontWeight: 500 }}>
                            {DAY_META[activeDay].label} · {DAY_META[activeDay].short}
                        </Typography>
                    </Box>
                </Box>

                <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: 2.5 } }}>
                    {/* ── Catch-up banner ── */}
                    {isCatchUp && (
                        <Alert severity="info" sx={{ mb: 2, borderRadius: "10px" }}>
                            Today's {DAY_META[todayDayNum] ? `Day ${todayDayNum}` : "challenge"} is waiting, but
                            first finish {DAY_META[activeDay].label} — {DAY_META[activeDay].short}.
                        </Alert>
                    )}

                    {/* ── Active day heading ── */}
                    {/* <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "10px", background: tokens.gradient, color: "#fff" }}>
                            {DAY_META[activeDay].icon}
                        </Box>
                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: "#2c3e50",
                                    fontWeight: 600,
                                    fontSize: "0.9rem",
                                    lineHeight: 1.2,
                                }}
                            >
                                {DAY_META[activeDay].label}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "#6B7280",
                                    fontWeight: 400,
                                    fontSize: "0.8rem",
                                }}
                            >
                                {DAY_META[activeDay].short}
                            </Typography>
                        </Box>
                    </Stack> */}

                    {/* ── Day-specific content ── */}
                    {activeDay === 1 && (
                        <QuizChallenge
                            step={quizStep}
                            setStep={setQuizStep}
                            answers={quizAnswers}
                            setAnswers={setQuizAnswers}
                            finished={quizFinished}
                            setFinished={setQuizFinished}
                            // onGoToMoodCheckin={() => {
                            //     handleCompleteAndAdvance(20, "campaign_selfcare_day1_moodcheckin");
                            //     navigate("/mood-checkin");
                            // }}
                            onGoToMoodCheckin={() => {
                                handleSaveCampaign(1, {
                                    quiz_answers: formatQuizAnswers(quizAnswers),
                                });

                                handleCompleteAndAdvance(1, "campaign_selfcare_day1_moodcheckin");
                                navigate("/mood-checkin");
                            }}
                        />
                    )}

                    {activeDay === 2 && (
                        <SoundscapeChallenge
                            // onChoose={(path) => {
                            //     handleSaveCampaign(2, {
                            //         soundscape_choice: SOUNDSCAPE_ID_MAP[path] || path,
                            //     });
                            //     handleCompleteAndAdvance(21, "campaign_selfcare_day2_soundscape");
                            //     navigate(path);
                            // }}
                            onChoose={handleSoundscapeChoice}
                        />
                    )}

                    {activeDay === 3 && (
                        <PodcastChallenge
                            feedback={podcastFeedback}
                            podcastPlayedMs={podcastPlayedMs}
                            onOpenPodcast={onOpenPodcast}
                            onSubmitFeedback={(value) => {
                                setPodcastFeedback(value);
                                handleSaveCampaign(3, { podcast_feedback: value });
                                handleCompleteAndAdvance(3, "campaign_selfcare_day3_podcast");
                            }}
                        />
                    )}

                    {activeDay === 4 && (
                        <QuickActionsChallenge
                            navigate={navigate}
                            onChoose={(key) => handleSaveCampaign(4, { quick_action: key })}
                            onComplete={() => handleCompleteAndAdvance(4, "campaign_selfcare_day4_quickaction")}
                        />
                    )}

                    {activeDay === 5 && (
                        <FinalPollChallenge
                            selections={pollSelections}
                            setSelections={setPollSelections}
                            // onSubmit={() => handleCompleteAndAdvance(24, "campaign_selfcare_day5_poll")}
                            onSubmit={() => {
                                handleSaveCampaign(5, {
                                    final_poll: pollSelections,
                                });

                                handleCompleteAndAdvance(5, "campaign_selfcare_day5_poll");
                            }}
                        />
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};


const CampaignHeader = () => (
    <>
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                bgcolor: tokens.primaryLight,
                borderRadius: "20px",
                px: 1.25,
                py: 0.3,
                mb: 0.5,
            }}
        >
            <SelfImprovement sx={{ fontSize: 12, color: tokens.primary }} />
            <Typography
                variant="caption"
                sx={{
                    fontWeight: 700,
                    color: tokens.primary,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    fontSize: "0.6rem",
                }}
            >
                Campaign
            </Typography>
        </Box>

        <Typography
            variant="h6"
            fontWeight={700}
            sx={{
                background: tokens.gradient,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 0.25,
            }}
        >
            Self-Care Unfiltered
        </Typography>

        <Typography
            variant="body2"
            sx={{
                color: "#6B7280",
                fontWeight: 400,
                mb: 0.5
            }}
        >
            5 Days of Challenging What We Think Self-Care Is
        </Typography>

        <Divider sx={{ borderColor: tokens.border, mb: 2 }} />
    </>
);



/* ─────────────────────────────────────────────────────────────────────────
   Day 1 — Myth vs Reality Quiz
   ───────────────────────────────────────────────────────────────────────── */
const QuizChallenge = ({ step, setStep, answers, setAnswers, finished, setFinished, onGoToMoodCheckin }) => {
    if (finished) {
        return (
            <Box>
                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ mb: 1.5 }}
                >
                    The Self-Care Reality Check: Myth vs Reality Quiz
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: tokens.textSecondary,
                        lineHeight: 1.6,
                        mb: 2,
                    }}
                >
                    <strong>How many myths did you bust?</strong> Self-care isn't just about rest, relaxation, or treating yourself — it's an intentional process of
                    recognizing your needs and taking actions that support your long-term wellbeing.
                </Typography>

                <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ mb: 1.5 }}
                >
                    Check In Before You Check Out
                </Typography>

                <Button
                    variant="contained"
                    endIcon={<ArrowForward fontSize="small" />}
                    onClick={onGoToMoodCheckin}
                    sx={{
                        background: tokens.gradient,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        boxShadow: "none",
                        animation: "ctaPulse 2.4s ease-in-out infinite",
                        "@keyframes ctaPulse": {
                            "0%,100%": { boxShadow: "0 0 0 0 rgba(47,158,111,0.45)" },
                            "50%": { boxShadow: "0 0 0 7px rgba(47,158,111,0)" },
                        },
                    }}
                >
                    Check Mood
                </Button>
            </Box>
        );
    }

    const question = QUIZ_QUESTIONS[step];
    const selected = answers[step];




    return (
        <Box>
            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ mb: 1.5 }}
            >
                The Self-Care Reality Check: Myth vs Reality Quiz
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.textSecondary, display: "block", mb: 0.5 }}>
                Question {step + 1} of {QUIZ_QUESTIONS.length}
            </Typography>
            <Typography
                variant="subtitle2"
                sx={{
                    color: "#2c3e50",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                    mb: 1,
                }}
            >
                {question.q}
            </Typography>

            <RadioGroup
                value={selected ?? ""}
                onChange={(e) => setAnswers({ ...answers, [step]: Number(e.target.value) })}
            >
                {question.options.map((opt, i) => (
                    <FormControlLabel
                        key={i}
                        value={i}
                        control={<Radio size="small" />}
                        label={
                            <Typography
                                variant="body2"
                                sx={{
                                    color: "text.secondary",
                                    lineHeight: 1.4,
                                }}
                            >
                                {opt}
                            </Typography>
                        }
                        sx={{
                            alignItems: "center",   // Centers radio with the text
                            mb: 0.5,
                            "& .MuiFormControlLabel-label": {
                                display: "flex",
                                alignItems: "center",
                            },
                        }}
                    />
                ))}
            </RadioGroup>

            <Stack direction="row" justifyContent="space-between" mt={1}>
                {step > 0 ? (
                    <Button
                        size="small"
                        startIcon={<ArrowBack fontSize="small" />}
                        onClick={() => setStep(step - 1)}
                        sx={{ textTransform: "none" }}
                    >
                        Back
                    </Button>
                ) : (
                    <Box />
                )}
                <Button
                    size="small"
                    variant="contained"
                    disabled={selected === undefined}
                    endIcon={<ArrowForward fontSize="small" />}
                    onClick={() =>
                        step === QUIZ_QUESTIONS.length - 1
                            ? setFinished(true)
                            : setStep(step + 1)
                    }
                    sx={{
                        background: tokens.gradient,
                        textTransform: "none",
                        boxShadow: "none",
                    }}
                >
                    Next
                </Button>
            </Stack>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────
   Day 2 — Soundscapes
   ───────────────────────────────────────────────────────────────────────── */


// const soundscapeButtons = [
//   {
//     label: "Rejuvenate with Echo Sounds",
//     path: "/rejuvenate-sounds",
//     image: RejuvenateSoundsLogo,
//     background:
//       "linear-gradient(135deg, #22c55e 0%, #16a34a 25%, #15803d 50%, #166534 75%, #14532d 100%)",
//   },
//   {
//     label: "Echo Soundscapes",
//     path: "/echo-soundscapes",
//     image: EchoSoundscapesLogo,
//     background:
//       "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 25%, #1e40af 50%, #1e3a8a 75%, #172554 100%)",
//   },
// ];

const soundscapeButtons = [
    {
        title: "Rejuvenate with Echo Sounds",
        subtitle: "Reset Energy",
        path: "/rejuvenate-sounds",
        image: RejuvenateSoundsLogo,
        gradient: "linear-gradient(135deg,#b27a42,#d39a4c)",
    },
    {
        title: "Echo Soundscapes",
        subtitle: "Calm Mind",
        path: "/echo-soundscapes",
        image: EchoSoundscapesLogo,
        gradient: "linear-gradient(135deg,#7b35d4,#a456ef)",
    },
];



const SoundscapeChallenge = ({ onChoose }) => (
    <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            TUNE INTO OUR SOUNDSCAPES!
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 2 }}>
            Self-care often begins by slowing down enough to notice what's happening
            within us. Choose one soundscape and listen at your own pace.
        </Typography>

        <Stack direction="row" spacing={1.5}>
            {soundscapeButtons.map((item) => (
                <Card
                    key={item.path}
                    onClick={() => onChoose(item.path)}
                    elevation={0}
                    sx={{
                        flex: 1,
                        minWidth: 0, // critical: allows flex children to shrink instead of overflowing
                        height: 88,
                        display: "flex",
                        borderRadius: "14px",
                        overflow: "hidden",
                        cursor: "pointer",
                        border: `1px solid ${tokens.border}`,
                        transition: "transform 0.15s ease",
                        "&:hover": { transform: "translateY(-2px)" },
                    }}
                >
                    {/* Left image */}
                    <Box
                        component="img"
                        src={item.image}
                        sx={{
                            width: 60,
                            height: "100%",
                            objectFit: "cover",
                            flexShrink: 0,
                        }}
                    />

                    {/* Right content */}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            background: item.gradient,
                            color: "#fff",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            px: 1.25,
                            gap: 0.25,
                        }}
                    >
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: 12.5,
                                lineHeight: 1.2,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                            }}
                        >
                            {item.title}
                        </Typography>

                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <MusicNote sx={{ fontSize: 12 }} />
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    opacity: 0.9,
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {item.subtitle}
                            </Typography>
                        </Stack>
                    </Box>
                </Card>
            ))}
        </Stack>
    </Box>
);
/* ─────────────────────────────────────────────────────────────────────────
   Day 3 — Podcast
   ───────────────────────────────────────────────────────────────────────── */
const PODCAST_REQUIRED_MS = 10 * 1000; // 10 seconds (testing) — change to 10 * 60 * 1000 for prod

const PodcastChallenge = ({ feedback, onOpenPodcast, onSubmitFeedback, podcastPlayedMs = 0 }) => {
    const podcastStarted = podcastPlayedMs > 0;
    const podcastEligible = podcastPlayedMs >= PODCAST_REQUIRED_MS;
    const remainingSec = Math.max(0, Math.ceil((PODCAST_REQUIRED_MS - podcastPlayedMs) / 1000));
    const mins = Math.floor(remainingSec / 60);
    const secs = remainingSec % 60;
    const progress = Math.min(100, (podcastPlayedMs / PODCAST_REQUIRED_MS) * 100);

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Choose SUPPORT instead of SCROLLING
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Self-Care Beyond Bubble Baths: What Taking Care of Ourselves Really Means
            </Typography>

            {!podcastStarted && (
                <>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 2 }}>
                        Spend at least 10 min engaging with this podcast on Self-care.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={onOpenPodcast}
                        sx={{
                            background: tokens.gradient, borderRadius: "8px", textTransform: "none", fontWeight: 600, boxShadow: "none",
                            animation: "ctaPulse 2.4s ease-in-out infinite",
                            "@keyframes ctaPulse": {
                                "0%,100%": { boxShadow: "0 0 0 0 rgba(47,158,111,0.45)" },
                                "50%": { boxShadow: "0 0 0 7px rgba(47,158,111,0)" },
                            },
                        }}
                    >
                        Listen to the Podcast
                    </Button>
                </>
            )}

            {podcastStarted && !podcastEligible && (
                <>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 1.5 }}>
                        Keep listening — feedback unlocks in <strong>{mins}:{String(secs).padStart(2, "0")}</strong>
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            mb: 2, height: 6, borderRadius: 3,
                            bgcolor: tokens.primaryLight,
                            "& .MuiLinearProgress-bar": { background: tokens.gradient },
                        }}
                    />
                    <Button
                        variant="outlined"
                        onClick={onOpenPodcast}
                        sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, borderColor: tokens.primary, color: tokens.primary }}
                    >
                        Continue Listening
                    </Button>
                </>
            )}

            {podcastEligible && (
                <>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 1 }}>
                        Let us know if you found the podcast
                    </Typography>
                    <RadioGroup
                        value={feedback || ""}
                        onChange={(e) => onSubmitFeedback(e.target.value)}
                    >
                        {["Helpful", "Somewhat Helpful", "Not for Me"].map((label) => (
                            <FormControlLabel
                                key={label}
                                value={label}
                                control={<Radio size="small" sx={{ color: tokens.primary, "&.Mui-checked": { color: tokens.primary } }} />}
                                label={<Typography variant="body2">{label}</Typography>}
                            />
                        ))}
                    </RadioGroup>
                </>
            )}
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────
   Day 4 — Quick Actions
   ───────────────────────────────────────────────────────────────────────── */
const QA_KEYS = ["gratitude", "stretch", "smile"];

const QuickActionsChallenge = ({ onChoose, onComplete, navigate }) => {
    const actions = allQuickActions.filter((a) => QA_KEYS.includes(a.key));
    const [selectedAction, setSelectedAction] = useState(null);

    const handleClick = (action) => {
        onChoose(action.key);
        if (action.key === "gratitude") {
            onComplete?.();
            navigate("/gratitude-checkin");
            return;
        }
        const sets = action.instructionSets;
        const instructions = sets[Math.floor(Math.random() * sets.length)];
        setSelectedAction({ ...action, instructions });
    };

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                A 5-minute RESET!
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 2 }}>
                Pick one Quick Action for a 5-minute reset.
            </Typography>
            <Stack spacing={1}>
                {actions.map((action) => (
                    <Button
                        key={action.key}
                        variant="outlined"
                        onClick={() => handleClick(action)}
                        sx={{ borderRadius: "10px", textTransform: "none", justifyContent: "flex-start", borderColor: tokens.border, color: "#111827" }}
                    >
                        {action.icon} {action.title.split(" ").slice(1).join(" ")}
                    </Button>
                ))}
            </Stack>
            {selectedAction && (
                <QuickActionModal
                    open={!!selectedAction}
                    onClose={() => {
                        setSelectedAction(null);
                        onComplete?.();
                    }}
                    action={selectedAction}
                />
            )}
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────
   Day 5 — Final poll
   ───────────────────────────────────────────────────────────────────────── */
const FinalPollChallenge = ({ selections, setSelections, onSubmit }) => {
    const toggle = (id) => {
        setSelections((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    return (
        <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Say it out Loud!
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 1.5 }}>
                Which part of the challenge did you find most useful? (choose all that apply)
            </Typography>
            <Stack>
                {FINAL_POLL_OPTIONS.map((opt) => (
                    <FormControlLabel
                        key={opt.id}
                        control={<Checkbox size="small" checked={selections.includes(opt.id)} onChange={() => toggle(opt.id)} />}
                        label={<Typography variant="body2">{opt.label}</Typography>}
                    />
                ))}
            </Stack>
            <Button
                variant="contained"
                disabled={selections.length === 0}
                onClick={onSubmit}
                sx={{
                    background: tokens.gradient, borderRadius: "8px", textTransform: "none", fontWeight: 600, boxShadow: "none", mt: 1.5,
                    animation: "ctaPulse 2.4s ease-in-out infinite",
                    "@keyframes ctaPulse": {
                        "0%,100%": { boxShadow: "0 0 0 0 rgba(47,158,111,0.45)" },
                        "50%": { boxShadow: "0 0 0 7px rgba(47,158,111,0)" },
                    },
                }}
            >
                Submit
            </Button>
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────────────────
   Post-campaign states
   ───────────────────────────────────────────────────────────────────────── */
// const WaitingCard = ({ nextDay }) => (
//     <Box sx={{ maxWidth: 560, mx: "auto", px: { xs: 1, sm: 0 } }}>
//         <Card elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${tokens.border}` }}>
//             <Box sx={{ height: 4, background: tokens.gradient }} />
//             <CardContent sx={{ p: 2.5 }}>
//                 <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
//                     Nice work — you're all caught up!
//                 </Typography>
//                 <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
//                     {DAY_META[nextDay]?.label} unlocks once it arrives. See you then.
//                 </Typography>
//             </CardContent>
//         </Card>
//     </Box>
// );


const WaitingCard = ({ nextDay }) => (
    <Box sx={{ maxWidth: 560, mx: "auto", px: { xs: 1, sm: 0 } }}>
        <Card elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${tokens.border}`, overflow: "hidden" }}>
            <Box sx={{ height: 5, background: tokens.gradient }} />
            <Box sx={{
                background: "linear-gradient(135deg, #f0faf5 0%, #ede9fb 100%)",
                px: 3, pt: 3, pb: 2.5,
                display: "flex", alignItems: "flex-start", gap: 2,
            }}>
                <Box sx={{
                    width: 48, height: 48, borderRadius: "14px", flexShrink: 0,
                    background: tokens.gradient,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 14px rgba(47,158,111,0.30)",
                }}>
                    <CheckCircle sx={{ fontSize: 26, color: "#fff" }} />
                </Box>
                <Box>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#111827", lineHeight: 1.3, mb: 0.4 }}>
                        You're all caught up! 🎉
                    </Typography>
                    <Typography variant="body2" sx={{ color: tokens.textSecondary, lineHeight: 1.5 }}>
                        Great work on today's challenge. Come back for{" "}
                        <Box component="span" sx={{ fontWeight: 700, color: tokens.primary }}>
                            {DAY_META[nextDay]?.label}
                        </Box>{" "}to keep the momentum going.
                    </Typography>
                </Box>
            </Box>
            <Divider sx={{ borderColor: tokens.border }} />
            <CardContent sx={{ px: 3, py: 2, "&:last-child": { pb: 2.5 } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: tokens.primary, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: tokens.textSecondary, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                        Next up · {DAY_META[nextDay]?.label}
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: "#374151", mt: 0.75, fontWeight: 500 }}>
                    {DAY_META[nextDay]?.short}
                </Typography>
            </CardContent>
        </Card>
    </Box>
);
const CongratsCard = () => (
    <Box sx={{ maxWidth: 560, mx: "auto", px: { xs: 1, sm: 0 } }}>
        <Card elevation={0} sx={{ borderRadius: "16px", border: `1px solid ${tokens.border}`, overflow: "hidden" }}>
            {/* Hero */}
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5, background: tokens.gradient, color: "#fff", position: "relative", overflow: "hidden" }}>
                <Box sx={{ position: "absolute", top: -24, right: -24, width: 110, height: 110, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.07)" }} />
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
                        bgcolor: "rgba(255,255,255,0.20)", backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255,255,255,0.32)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                        <CheckCircle sx={{ fontSize: 20, color: "#fff" }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={800}>
                        Congratulations! 🎊
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ opacity: 0.88, lineHeight: 1.5 }}>
                    You've completed all 5 days of{" "}
                    <Box component="span" sx={{ fontWeight: 700 }}>Self-Care Unfiltered</Box>.
                    Here's what to carry forward:
                </Typography>
            </Box>

            <CardContent sx={{ px: 2.5, pt: 1.5, pb: 2, "&:last-child": { pb: 2 } }}>
                <Typography variant="caption" sx={{
                    color: tokens.textSecondary, fontWeight: 700,
                    textTransform: "uppercase", letterSpacing: "0.06em", display: "block", mb: 1,
                }}>
                    Your Self-Care Truths
                </Typography>
                <Stack spacing={0.75}>
                    {REMINDERS.map((r) => (
                        <Box key={r} sx={{ display: "flex", alignItems: "baseline", gap: 1.25 }}>
                            <Box sx={{
                                width: 6, height: 6, borderRadius: "50%",
                                bgcolor: tokens.primary, flexShrink: 0,
                                position: "relative", top: "1px",
                            }} />
                            <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.5 }}>
                                {r}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    </Box>
);

export default SelfCareCampaign;